export type Authority = 'DISTRICT' | 'REGIONAL';
export type IntakeStatus = 'PLANNED' | 'ACTIVE' | 'ARCHIVED' | 'DECOMMISSIONED';
/** Rodzaj ujęcia: nowe (projektowane) vs istniejące. */
export type IntakeKind = 'NEW' | 'EXISTING';
export type StudyStatus =
  | 'NEW' | 'IN_PROGRESS' | 'OFFICE_WORK' | 'AI_VERIFICATION' | 'HUMAN_VERIFICATION'
  | 'SIGNATURE' | 'SENT'
  // AWAITING_REVIEW — po K12 wysyłce, czeka na decyzję urzędu (drafty zablokowane).
  // REVISION_NEEDED — urząd zwrócił z poprawkami, drafty wracają do edycji + reset K11/K12.
  | 'AWAITING_REVIEW' | 'REVISION_NEEDED'
  | 'COMPLETED' | 'CANCELLED';
export type Priority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW';
export type StepStatus = 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
export type TaskStatus =
  | 'TODO' | 'IN_PROGRESS' | 'AI_VERIFICATION' | 'HUMAN_VERIFICATION' | 'COMPLETED';
export type TaskType =
  | 'FIELD_WORK' | 'OFFICE_WORK' | 'ANALYSIS' | 'AI_VERIFICATION' | 'VERIFICATION' | 'SIGNATURE' | 'SHIPPING' | 'OTHER';
export type BoreholeFunction = 'PRODUCTION' | 'OBSERVATION' | 'REPLACEMENT';
export type BoreholeStatus = 'PLANNED' | 'DRILLING' | 'DRILLED' | 'ACTIVE' | 'DECOMMISSIONED';
export type LicenseCategory = 'NONE' | 'KAT_II' | 'KAT_III' | 'KAT_IV' | 'KAT_V';
export type Role = 'OWNER' | 'GEOLOGIST' | 'ASSISTANT' | 'OFFICE' | 'ADMIN';
export type ContractVariant =
  | 'SIMPLE' | 'COMPLEX' | 'VERY_COMPLEX' | 'VC_ZONES' | 'VC_PUMPING' | 'VC_MONITORING';
export type DraftStatus =
  | 'GENERATED' | 'EDITING' | 'ACCEPTED' | 'REJECTED' | 'REGENERATED';
export type OfferStatus =
  | 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED' | 'ORDERED';
export type OfferType = 'LUMP_SUM' | 'LINE_ITEMS';

/** Sposób renderowania sekcji treści oferty (V89). */
export type OfferSectionRenderType = 'RICH_TEXT' | 'NUMBERED_LIST' | 'BULLET_LIST' | 'SPEC_TABLE';
export type OfferKindPurpose = 'STUDY' | 'FIELDWORK' | 'OTHER';
export type OfferFollowUp = 'CONTRACT' | 'ORDER' | 'STUDY' | 'NONE';

export interface OfferSection {
  id?: number;
  position?: number;
  heading?: string;
  renderType: OfferSectionRenderType;
  content?: string;
}

export interface OfferTemplate {
  id: number;
  code: string;
  name: string;
  workType: string;
  description?: string;
  sampleSubject?: string;
  sampleBody?: string;
  sampleLegalBasis?: string;
  sampleTerms?: string;
  sampleExtraCosts?: string;
  styleNotes?: string;
  active: boolean;
}

export interface WorkType {
  code: string;
  label: string;
}

export interface AiGeneratedOffer {
  subjectHtml?: string;
  bodyHtml?: string;
  legalBasisHtml?: string;
  termsHtml?: string;
  extraCostsHtml?: string;
  provider: string;
  model: string;
  templatesUsed: number;
}

export interface Company {
  id: number;
  code: string;
  fullName: string;
  shortName: string;
  addressLine1?: string;
  addressLine2?: string;
  addressPostalCode?: string;
  addressCity?: string;
  issuePlace: string;
  phone?: string;
  email?: string;
  website?: string;
  nip?: string;
  regon?: string;
  bankAccount?: string;
  logoUrl?: string;
  signatureUrl?: string;
  closingText: string;
  isDefault: boolean;
  active: boolean;
}

/** Grupa pozycji kosztorysu (V173) — jedna grupa = jedno zlecenie po konwersji na umowę. */
export interface OfferLineGroup {
  id?: number;
  position?: number;
  label: string;
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null;
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
  deadlineLabel?: string | null;
}

export interface OfferLineItem {
  id?: number;
  position?: number;
  description: string;
  /** Etykieta grupy pozycji, np. nazwa studni — pozycje z tą samą etykietą dostają
   *  na piśmie wspólny nagłówek i podsumę. Puste = pozycja poza grupami (V172). */
  groupLabel?: string | null;
  unit: string;
  quantity: number;
  unitPriceNet: number;
  lineTotalNet?: number;
  /** Powiązanie z typem opracowania ze słownika cen — nullable dla pozycji ad-hoc (dojazd, opłaty). */
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null; // YYYY-MM-DD
  // Termin pozycji jako data / kwartał / opis (V160)
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
  /** Termin gotowy do wyświetlenia („II kwartał 2026”) — liczony przez serwer. */
  deadlineLabel?: string | null;
}

/** Sposób podania terminu realizacji (V160). */
export type OfferDeadlineMode = 'DATE' | 'QUARTER' | 'TEXT';

// ── Model pisma ofertowego (V160) ──────────────────────────────────────────
// Serwer przysyła gotowe bloki, z których powstaje PDF i DOCX. Podgląd ma je
// TYLKO narysować — składanie własnych zdań we froncie odtworzyłoby rozjazd
// podglądu z pobranym plikiem, który V160 właśnie likwiduje.

export interface DocRun {
  text: string;
  bold: boolean;
  italic: boolean;
  strike: boolean;
  /** Twarde złamanie wiersza (&lt;br&gt;); wtedy `text` jest pusty. */
  lineBreak: boolean;
}

export interface DocListItem {
  level: number;
  /** Punktor albo numer wyliczony przez serwer — front go nie generuje. */
  marker: string;
  runs: DocRun[];
}

export interface DocRow {
  key: string;
  value: string;
}

/** Tabela kosztorysu — kolumny, wyrównanie i teksty komórek wylicza serwer. */
export interface DocCostTable {
  columns: { title: string; width: number; numeric: boolean }[];
  /** ITEM: komórka na kolumnę; GROUP: [etykieta]; SUBTOTAL/TOTAL/GRAND_TOTAL: [etykieta, kwota]. */
  rows: { kind: 'ITEM' | 'GROUP' | 'SUBTOTAL' | 'TOTAL' | 'GRAND_TOTAL'; cells: string[] }[];
}

export interface DocBlock {
  kind: 'PARAGRAPH' | 'HEADING' | 'LIST' | 'SPEC_TABLE' | 'COST_TABLE' | 'RULE' | 'SPACER';
  align: 'LEFT' | 'CENTER' | 'RIGHT';
  runs: DocRun[];
  level: number;
  /** PARAGRAPH: skala tekstu; SPACER: wysokość w wierszach. */
  scale: number;
  ordered: boolean;
  items: DocListItem[];
  rows: DocRow[];
  /** COST_TABLE; poza nim null. */
  costTable?: DocCostTable | null;
}

export interface OfferDocumentHeader {
  fullName: string;
  addressLine?: string | null;
  legalLine?: string | null;
  logoUrl?: string | null;
}

export interface OfferDocument {
  header?: OfferDocumentHeader | null;
  dateLine: string;
  blocks: DocBlock[];
  signatureUrl?: string | null;
  signatureLabels: string[];
}

export interface OfferCompanySummary {
  id: number;
  shortName: string;
  fullName: string;
  issuePlace: string;
  logoUrl?: string;
  /** Pieczątka z podpisem firmy (V160). */
  signatureUrl?: string;
}

export interface Offer {
  id: number;
  number: string;
  type: OfferType;
  typeName: string;
  title: string;
  scope?: string;
  status: OfferStatus;
  statusName: string;
  client: Client;
  intakeId?: number;
  intakeName?: string;
  /** Pełna lista ujęć objętych ofertą (V71). intakeId/intakeName = pierwsze z listy. */
  intakes?: { id: number; name: string; number?: string; town?: string | null; wells?: string[] }[];
  company?: OfferCompanySummary;
  recipientHtml?: string;
  subjectHtml?: string;
  bodyHtml?: string;
  legalBasisHtml?: string;
  termsHtml?: string;
  extraCostsHtml?: string;
  unitPrice?: number;
  unitCount?: number;
  unitLabel?: string;
  /** Tylko dla LUMP_SUM — typ opracowania całej oferty (LINE_ITEMS używa pól na pozycji). */
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null;
  // Termin realizacji jako data / kwartał / opis (V160)
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
  printDeadline?: boolean;
  /** Termin gotowy do wyświetlenia („II kwartał 2026”) — liczony przez serwer. */
  deadlineLabel?: string | null;
  issueDate: string;
  validUntil?: string;
  paymentTerms?: string;
  vatRate: number;
  totalNet: number;
  totalVat: number;
  totalGross: number;
  notes?: string;
  sentAt?: string;
  decidedAt?: string;
  createdAt?: string;
  convertedContractId?: number;
  convertedStudyId?: number;
  lineGroups: OfferLineGroup[];
  lineItems: OfferLineItem[];
  // Rodzaj oferty + powiązany otwór + sekcje treści (V89)
  kindId?: number;
  kindName?: string;
  kindFollowUp?: OfferFollowUp;
  kindFollowUpName?: string;
  kindSecondSignatureLabel?: string;
  boreholeId?: number;
  boreholeDesignation?: string;
  /** Studnie ujęcia objęte ofertą (V187); pierwsza = główna. */
  boreholes?: { id: number; designation: string; intakeId: number | null }[];
  sections?: OfferSection[];
  /** Czy kwota brutto trafia do treści pisma (V160). */
  showGross?: boolean;
  /** Czy pieczątka z podpisem trafia do pisma (V160). */
  includeSignature?: boolean;
  /** Wyrenderowane pismo — to samo, z czego powstaje PDF i DOCX. Brak na listach ofert. */
  documentView?: OfferDocument | null;
}

export interface User {
  id: number;
  login: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  email?: string;
  roles: Role[];
  /** Spłaszczone uprawnienia (union po rolach z RolePermission) — używane do warunkowego UI:
   *  PRICING_VIEW, OFFER_VIEW, CONTRACT_VIEW, AI_VIEW_COSTS, ADMIN_*, itp. */
  permissions?: string[];
  category: LicenseCategory;
  active: boolean;
}

/** Helper — czy bieżący user ma dane uprawnienie (z user.permissions). Bezpieczne dla undefined. */
export function hasPermission(user: User | null | undefined, permission: string): boolean {
  if (!user || !user.permissions) return false;
  return user.permissions.includes(permission);
}

export interface Client {
  id: number;
  name: string;
  nip?: string;
  phone?: string;
  email?: string;
  addressLine1?: string;
  city?: string;
  postalCode?: string;
  voivodeship?: string;
  county?: string;
  gmina?: string;
  /** Punkt klienta na mapie (WGS84). Wpisywany ręcznie — adres rejestrowy bywa biurem gdzie
   *  indziej niż zakład, do którego naprawdę się jeździ. */
  latitude?: number | null;
  longitude?: number | null;
  /** Co oznacza pinezka: „biuro", „baza techniczna", „SUW Kępy". */
  locationNote?: string | null;
  remarks?: string;
  /** Profil relacji (V181) — zapis przez /api/clients/{id}/profile. */
  shortName?: string | null;
  clientType?: string | null;
  clientTypeName?: string | null;
  clientStatus?: string | null;
  clientStatusName?: string | null;
}

export interface BoreholeLayer {
  id: number;
  fromDepth: number;
  toDepth: number;
  symbol: string;
  description: string;
  color?: string;
  pattern?: string | null;
  lithologyTypeId?: number | null;
  lithologyTypeCode?: string | null;
  lithologyTypeName?: string | null;
}

export interface BoreholeHousing {
  typeId: number | null;
  typeCode: string | null;
  typeName: string | null;
  diameterMm: number | null;
  depth: number | null;
  cover: string | null;
  steelLadderDiameterMm: number | null;
  hydraulicFittings: string | null;
}

/** Faza 3 (V56) — strukturyzowane dane otworu do operatu wodnoprawnego. */
export interface BoreholeOperatData {
  filterFromDepth?: number | null;
  filterToDepth?: number | null;
  filterType?: string | null;
  filterDiameterMm?: number | null;
  pumpType?: string | null;
  pumpModel?: string | null;
  pumpInstallDepth?: number | null;
  pumpDeliveryPipeDiameterMm?: number | null;
  pumpMaxCapacityM3h?: number | null;
  meterType?: string | null;
  meterModel?: string | null;
  meterAccuracy?: string | null;
  meterLocation?: string | null;
  meterLastCalibration?: string | null;
  meterNextCalibration?: string | null;
  pumpingTestQM3h?: number | null;
  pumpingTestDrawdownM?: number | null;
  pumpingTestSpecificCapacity?: number | null;
  pumpingTestDurationH?: number | null;
  pumpingTestDate?: string | null;
  permissibleDrawdownM?: number | null;
  permeabilityKMs?: number | null;
  transmissivityTM2s?: number | null;
}

export interface Borehole {
  id: number;
  designation: string;
  function: BoreholeFunction;
  functionName: string;
  functionAbbreviation: string;
  status: BoreholeStatus;
  totalDepth?: number;
  diameterMm?: number;
  yieldM3h?: number;
  latitude?: number;
  longitude?: number;
  yearCompleted?: number;
  /** Średniodobowy pobór w m³/dobę. Sumowany przez wszystkie studnie ujęcia ×365 = roczny pobór. */
  avgDailyIntakeM3?: number;
  /** null jeśli żadne pole obudowy nie zostało wypełnione. */
  housing: BoreholeHousing | null;
  layers: BoreholeLayer[];
  remarks?: string;
  /** Strukturyzowane dane do operatu wodnoprawnego (Faza 3). Wszystkie pola opcjonalne. */
  operat?: BoreholeOperatData;
}

export interface StudySummary {
  id: number;
  number: string;
  title: string;
  type: string;
  typeName: string;
  typeAbbreviation: string;
  typeCode: string;
  intakeId?: number;
  intakeName?: string;
  /** Zamawiający — klient ujęcia. */
  clientId?: number | null;
  clientName?: string | null;
  authority: Authority;
  authorityName: string;
  status: StudyStatus;
  statusName: string;
  statusColor: string;
  priority: Priority;
  priorityName: string;
  deadline?: string;
  dispatchDate?: string | null;
  budget?: number;
  spent?: number;
  managerFirstName?: string;
  geologistFirstName?: string;
  currentStepCode?: string;
  currentStepName?: string;
  currentStepStatus?: string;
  currentAssigneeFullName?: string;
  currentAssigneeInitials?: string;
  /** Wskaźnik kompletności wymaganych dokumentów. null gdy opracowanie nie ma checklisty. */
  requiredDocsTotal?: number | null;
  requiredDocsReceived?: number | null;
  requiredDocsOverdue?: number | null;
  createdAt?: string;
}

/** Cele korzystania z wód — używany w sekcji "Cel i zakres" operatu. */
export type WaterUsePurpose =
  | 'DRINKING_WATER_SUPPLY'
  | 'INDUSTRIAL'
  | 'AGRICULTURAL'
  | 'FISH_FARMING'
  | 'SPA_OR_RECREATIONAL'
  | 'OTHER';

/** Typ formy ochrony przyrody. */
export type ProtectedAreaType =
  | 'NATURA_2000_OSO'
  | 'NATURA_2000_SOO'
  | 'PARK_NARODOWY'
  | 'PARK_KRAJOBRAZOWY'
  | 'REZERWAT_PRZYRODY'
  | 'OCHK'
  | 'POMNIK_PRZYRODY'
  | 'STANOWISKO_DOKUMENTACYJNE'
  | 'UZYTEK_EKOLOGICZNY'
  | 'ZESPOL_PRZYRODNICZO_KRAJOBRAZOWY';

/** Faza 3 (V56) — strukturyzowane dane ujęcia do operatu wodnoprawnego. */
export interface IntakeOperatData {
  jcwpdCode?: string | null;
  jcwpdName?: string | null;
  regionWodny?: string | null;
  dorzecze?: string | null;
  waterUsePurpose?: WaterUsePurpose | null;
  waterUsePurposeName?: string | null;
  waterUseDescription?: string | null;
  waterRecipients?: string | null;
  requestedPermitYears?: number | null;
  approvedResourceM3h?: number | null;
  approvedResourceDecisionRef?: string | null;
  approvedResourceDecisionDate?: string | null;
  approvedResourceDecisionAuthority?: string | null;
  previousPermitRef?: string | null;
  previousPermitDate?: string | null;
  previousPermitAuthority?: string | null;
  previousPermitExpiry?: string | null;
  treatmentDescription?: string | null;
  distributionDescription?: string | null;
  radiusM?: number | null;
  radiusMethod?: string | null;
  radiusCalculatedAt?: string | null;
  inFloodHazardArea?: boolean | null;
  inGzwp?: boolean | null;
  gzwpNumber?: number | null;
  gzwpName?: string | null;
}

export interface ProtectedAreaReference {
  id: number;
  intakeId: number;
  type: ProtectedAreaType;
  typeDisplayName: string;
  name: string;
  code?: string | null;
  distanceM?: number | null;
  objectOfProtection?: string | null;
  impactAssessment?: string | null;
  manuallyAdded?: boolean | null;
}

export interface Intake {
  id: number;
  number: string;
  name: string;
  client: Client;
  qM3h: number | null;
  authority: Authority;
  authorityName: string;
  authorityJustification?: string;
  status: IntakeStatus;
  kind: IntakeKind;
  kindName: string;
  latitude?: number;
  longitude?: number;
  town?: string;
  county?: string;
  voivodeship?: string;
  gmina?: string;
  validFrom?: string;
  createdAt?: string;
  zarzadZlewni?: {
    id: number;
    code: string;
    name: string;
    rzgwCode: string;
    rzgwName: string;
    addressLine1: string | null;
    addressPostalCode: string | null;
    addressCity: string | null;
  } | null;
  boreholes: Borehole[];
  studies: StudySummary[];
  /** Strukturyzowane dane do operatu wodnoprawnego (Faza 3, V56). */
  operat?: IntakeOperatData;
}

export interface StudyStep {
  id: number;
  code: string;
  codeString: string;
  name: string;
  description: string;
  stepNumber: number;
  status: StepStatus;
  assigneeId?: number | null;
  assigneeFirstName?: string;
  assigneeInitials?: string;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
}

export interface Task {
  id: number;
  title: string;
  description?: string;
  studyId?: number;
  studyNumber?: string;
  studyTitle?: string;
  /** Typ opracowania (kod StudyType) — z opracowania, a bez niego z zadania. */
  studyType?: string | null;
  workflowStep?: string;
  stepCode?: string;
  type: TaskType;
  status: TaskStatus;
  statusName: string;
  priority: Priority;
  priorityName: string;
  assigneeId?: number;
  assigneeFirstName?: string;
  assigneeInitials?: string;
  assigneeFullName?: string;
  createdById?: number;
  createdByFullName?: string;
  deadline?: string;
  position: number;
  createdAt?: string;
  statusChangedAt?: string;
  completedAt?: string;
}

/** Payload tworzenia/edycji zadania (POST /api/tasks, PUT /api/tasks/{id}). */
export interface TaskInput {
  title: string;
  description?: string | null;
  studyId?: number | null;
  type: TaskType;
  priority: Priority;
  deadline?: string | null;
  assigneeId?: number | null;
  status?: TaskStatus;
  /** Typ opracowania dla zadania bez opracowania — po nim tablica dobiera osoby do przyjęcia. */
  studyType?: string | null;
}

export interface Contract {
  id: number;
  number: string;
  studyId: number;
  variant: ContractVariant;
  variantName: string;
  signedDate?: string;
  amount?: number;
  contentMarkdown?: string;
  templateVersion?: string;
  signed: boolean;
  acceptedAt?: string | null;
  draft?: boolean;
  aiGenerated?: boolean;
}

export interface StudyDetail {
  summary: StudySummary;
  intake: Intake;
  steps: StudyStep[];
  tasks: Task[];
  drafts: AiDraft[];
  /** Pełna umowa — tylko z uprawnieniem CONTRACT_VIEW. */
  contract?: Contract | null;
  /** Najważniejsze z umowy bez kwot — dla każdego, kto widzi temat. */
  contractHighlights?: ContractHighlights | null;
  percent: number;
}

export interface ContractHighlights {
  number: string;
  status: string;
  signedDate: string | null;
  items: { label: string; value: string; tone: 'warn' | 'danger' | null }[];
}

export interface AiDraft {
  id: number;
  studyId: number;
  studyNumber: string;
  section: string;
  draftText: string;
  editedText?: string;
  prompt?: string;
  sourcesJson?: string;
  status: DraftStatus;
  model: string;
  inputTokens?: number;
  outputTokens?: number;
  generationTimeMs?: number;
  createdAt: string;
  /** Pełne imię i nazwisko użytkownika, który zaakceptował draft. Null gdy nie zatwierdzony. */
  acceptedByName?: string | null;
  acceptedAt?: string | null;
}

export interface StudyType {
  id: string;
  code: string;
  name: string;
  abbreviation: string;
  variantCount: number;
  variants: string[];
  requiresCategoryV: boolean;
}

export interface MapLayer {
  id: number;
  code: string;
  name: string;
  source: string;
  protocol: string;
  url: string;
  layerId?: string;
  imageFormat?: string;
  /** WMTS: identyfikator zestawu macierzy kafelków (TILEMATRIXSET, np. EPSG:3857). */
  tileMatrixSet?: string | null;
  /** WMTS: styl warstwy (STYLE w GetTile, zwykle „default"). */
  wmtsStyle?: string | null;
  category: string;
  defaultOpacity: number;
  enabledByDefault: boolean;
}

/** Warstwa WMS/WMTS wymagana/zalecana dla danego typu opracowania (słownik admina). */
export interface StudyTypeRequiredLayer {
  id: number;
  studyType: string;
  mapLayerId: number;
  layerCode: string;
  layerName: string;
  layerProtocol: string;
  layerCategory: string;
  layerSource: string;
  mandatory: boolean;
  note?: string | null;
  sortOrder: number;
  active: boolean;
}

export interface DashboardData {
  today: string;
  activeIntakes: number;
  districtIntakes: number;
  regionalIntakes: number;
  studiesInProgress: number;
  officeWork: number;
  toBeSigned: number;
  deadlinesThisWeek: StudySummary[];
  criticalDeadlines: number;
  draftsThisWeek: number;
  draftsAccepted: number;
  /**
   * Realny szereg 7 dni z dziennym licznikiem draftów AI (do sparkline na Dashboardzie).
   * Pierwszy element = 7 dni temu, ostatni = dzisiaj.
   */
  aiDraftsByDay: { date: string; dayLabel: string; count: number }[];
  typesCount: Record<string, number>;
  /**
   * Chuda projekcja ujęć — NIE pełny `Intake`. Dashboard celowo nie zwraca klienta z NIP-em,
   * e-mailem, współrzędnych ani listy otworów: widget renderuje siedem pól, a payload trafia
   * do każdego z INTAKE_VIEW. `clientName` jest puste bez uprawnienia CLIENT_VIEW.
   */
  topIntakes: DashboardIntake[];
  /** Zadania przypisane do ZALOGOWANEGO użytkownika (nie wszystkie w firmie). */
  myTasks: Task[];
  archiveCount: number;
  /** Terminy compliance nadchodzące ≤30 dni lub po terminie (przeglądy/BHP/ubezpieczenia). */
  complianceDeadlines: ComplianceDeadline[];
  /** Liczba terminów compliance po terminie. */
  complianceOverdue: number;
  /** Terminy Kadr (badania/szkolenia/uprawnienia + końce umów) ≤30 dni lub po terminie. */
  hrDeadlines: DeadlineRow[];
  hrOverdue: number;
  /** Terminy Pojazdów (badania techniczne, OC/AC, UDT, tachograf, przeglądy) ≤30 dni lub po terminie. */
  fleetDeadlines: DeadlineRow[];
  fleetOverdue: number;
  /** Najbliższe zaplanowane wyjazdy brygad. */
  upcomingTrips: UpcomingTrip[];
  /** Nierozliczone okresy płatności cyklicznych ≤30 dni lub po terminie (V153). Puste bez FINANCE_VIEW. */
  subscriptionPayments: SubscriptionPeriod[];
  subscriptionOverdue: number;
  /** Ile okresów po terminie nie ma przypiętej faktury, mimo że jest oczekiwana. */
  subscriptionMissingInvoices: number;
  /** `null` gdy użytkownik nie ma DRIVE_VIEW. */
  driveStats: { mode: string; rootFolder: string; totalFiles: number; indexedFiles: number; filesToIndex: number; percent: number } | null;
}

/** Ujęcie w kształcie, w jakim wystawia je dashboard (patrz DashboardData.topIntakes). */
export interface DashboardIntake {
  id: number;
  name: string;
  authority: Authority | null;
  authorityName: string | null;
  qM3h: number | null;
  boreholeCount: number;
  clientName: string | null;
}

export type ComplianceCategory = 'INSPECTION' | 'BHP' | 'INSURANCE' | 'WATER_MONITORING' | 'OTHER';
export type ComplianceStatus = 'ACTIVE' | 'DONE' | 'CANCELLED';
export type ComplianceUrgency = 'OK' | 'DUE_SOON' | 'OVERDUE';

/** Termin do pilnowania (przeglądy, BHP, ubezpieczenia) — moduł „Terminy" (V72). */
export interface ComplianceDeadline {
  id: number;
  category: ComplianceCategory;
  categoryName: string;
  title: string;
  description?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  userId?: number | null;
  userName?: string | null;
  responsibleUserId?: number | null;
  responsibleUserName?: string | null;
  dueDate: string;
  recurrenceMonths?: number | null;
  remindDaysBefore: number;
  lastCompletedDate?: string | null;
  status: ComplianceStatus;
  statusName: string;
  statusColor: string;
  daysUntil?: number | null;
  urgency: ComplianceUrgency;
}

export interface ArchiveDocument {
  id: number;
  name: string;
  drivePath?: string;
  studyType?: string;
  typeCode?: string;
  year?: number;
  intakeName?: string;
  clientName?: string;
  sizeBytes?: number;
  pages?: number;
  indexed: boolean;
}

export interface AuditEntry {
  id: number;
  eventTime: string;
  eventType: string;
  actorLogin?: string;
  actorName?: string;
  /** Administrator, który wykonał akcję wcielony w actorLogin (tryb „pracuj jako"); brak = zwykła sesja. */
  impersonatorLogin?: string;
  objectType?: string;
  objectId?: number;
  description: string;
  currentHash: string;
  previousHash: string;
}

export interface AuthorityProposal {
  authority: Authority;
  authorityName: string;
  category: LicenseCategory;
  typicalDays: number;
  justification: string;
  threshold: number;
}

export interface VariantRecommendation {
  recommended: string;
  variants: {
    variant: string;
    displayName: string;
    minPages: number;
    maxPages: number;
    recommended: boolean;
    available: boolean;
  }[];
  features: string[];
}

// ════════════════════════════════════════════════════════════════════════
// Kadry / HR · Pojazdy/Sprzęt · Kalendarz brygad (V77)
// ════════════════════════════════════════════════════════════════════════

export type Urgency = 'OK' | 'DUE_SOON' | 'OVERDUE';

/** Ujednolicony wiersz „nadchodzącego terminu" na dashboardzie (Kadry/Pojazdy). */
export interface DeadlineRow {
  source: string;       // HR | FLEET
  sourceName: string;   // Kadry | Pojazdy
  label: string;
  sublabel: string;
  dueDate: string;
  daysUntil: number;
  urgency: Urgency;
  link: string;
}

// ── Kadry / HR ──────────────────────────────────────────────────────────
export type EmploymentStatus = 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';
export type ContractType = 'UOP_INDEFINITE' | 'UOP_FIXED' | 'ZLECENIE' | 'DZIELO' | 'B2B';
export type QualificationType = 'MEDICAL' | 'BHP' | 'TRAINING' | 'LICENSE' | 'OTHER';
export type QualificationStatus = 'VALID' | 'EXPIRED' | 'ARCHIVED';

export interface EmploymentContract {
  id: number;
  type: ContractType;
  typeName: string;
  startDate: string;
  endDate?: string | null;
  position?: string | null;
  fte?: number | null;
  wage?: number | null;   // tylko OWNER/ADMIN
  notes?: string | null;
  daysUntil?: number | null;
  urgency: Urgency;
}

export interface EmployeeQualification {
  id: number;
  type: QualificationType;
  typeName: string;
  name: string;
  issuer?: string | null;
  issueDate?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  remindDaysBefore: number;
  status: QualificationStatus;
  statusName: string;
  statusColor: string;
  notes?: string | null;
  daysUntil?: number | null;
  urgency: Urgency;
}

export interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  position: string;
  department?: string | null;
  phone?: string | null;
  email?: string | null;
  userId?: number | null;
  userName?: string | null;
  hireDate?: string | null;
  birthDate?: string | null;
  pesel?: string | null;   // tylko OWNER/ADMIN
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  emergencyContact?: string | null;
  employmentStatus: EmploymentStatus;
  employmentStatusName: string;
  employmentStatusColor: string;
  companyId?: number | null;
  companyName?: string | null;
  notes?: string | null;
  contracts: EmploymentContract[];
  qualifications: EmployeeQualification[];
  nearestDaysUntil?: number | null;
  nearestUrgency: Urgency;
  nearestDeadlineLabel?: string | null;
}

export interface EmployeeInput {
  firstName: string;
  lastName: string;
  position: string;
  department?: string | null;
  phone?: string | null;
  email?: string | null;
  userId?: number | null;
  hireDate?: string | null;
  birthDate?: string | null;
  pesel?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  emergencyContact?: string | null;
  employmentStatus?: EmploymentStatus;
  companyId?: number | null;
  notes?: string | null;
}

export interface ContractInput {
  type: ContractType;
  startDate: string;
  endDate?: string | null;
  position?: string | null;
  fte?: number | null;
  wage?: number | null;
  notes?: string | null;
}

export interface QualificationInput {
  type: QualificationType;
  name: string;
  issuer?: string | null;
  issueDate?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  remindDaysBefore?: number | null;
  status?: QualificationStatus;
  notes?: string | null;
}

export interface EmployeeMeta {
  employmentStatuses: { id: EmploymentStatus; name: string; color: string }[];
  contractTypes: { id: ContractType; name: string }[];
  qualificationTypes: { id: QualificationType; name: string }[];
  qualificationStatuses: { id: QualificationStatus; name: string; color: string }[];
  dayTypes: { id: DayType; name: string; color: string }[];
}

// ── Ewidencja czasu pracy / nadgodziny (V79) ─────────────────────────────
export type DayType = 'WORK' | 'VACATION_UW' | 'SICK' | 'HOLIDAY' | 'UNPAID' | 'OTHER';
export type SettlementStatus = 'DRAFT' | 'FINALIZED';

export interface WorkTimeEntry {
  id: number;
  workDate: string;
  startTime?: string | null;   // HH:mm:ss
  endTime?: string | null;
  dayType: DayType;
  dayTypeName: string;
  dayTypeColor: string;
  hoursWorked: number;
  delegationValue: number;
  vehicleId?: number | null;
  vehicleLabel?: string | null;
  location?: string | null;
  notes?: string | null;
}

export interface WorkTimeEntryInput {
  workDate: string;
  startTime?: string | null;
  endTime?: string | null;
  dayType: DayType;
  delegationValue?: number | null;
  vehicleId?: number | null;
  location?: string | null;
  notes?: string | null;
}

/** Miesięczne rozliczenie (PODSUMOWANIE). Kwoty null dla OFFICE (redakcja po roli). */
export interface MonthlySettlement {
  id?: number | null;
  year: number;
  month: number;
  normHours: number;
  workedHours: number;
  overtimeHours: number;
  holidayHours: number;
  delegationCount: number;
  hourlyPay?: number | null;
  overtimePremiumPay?: number | null;
  delegationPay?: number | null;
  lunchCard?: number | null;
  insurance?: number | null;
  baseSalary?: number | null;
  cashTopUp?: number | null;
  carryoverIn?: number | null;
  paidCash?: number | null;
  carryoverOut?: number | null;
  status: SettlementStatus;
  statusName: string;
  statusColor: string;
  notes?: string | null;
}

export interface SettlementInput {
  normHours?: number | null;
  paidCash?: number | null;
  notes?: string | null;
}

export interface EmployeePayroll {
  id?: number | null;
  hourlyRate?: number | null;      // złotowe pola null dla OFFICE
  delegationRate?: number | null;
  lunchCardMonthly?: number | null;
  insuranceMonthly?: number | null;
  overtimePct?: number | null;
  sundayHolidayPct?: number | null;
}

export type EmployeePayrollInput = Omit<EmployeePayroll, 'id'>;

export interface Timesheet {
  employeeId: number;
  employeeName: string;
  year: number;
  month: number;
  entries: WorkTimeEntry[];
  settlement: MonthlySettlement;
  payroll: EmployeePayroll;
}

// ── Dwutygodniówka ze zdjęcia (AI OCR) ──────────────────────────────────
export interface DwuDayRow {
  date: string;
  dayName: string;
  startTime?: string | null;
  endTime?: string | null;
  hours?: number | null;
  delegation?: number | null;
  location?: string | null;
  notes?: string | null;
}
export interface DwutygodniowkaExtraction {
  provider: string;
  model: string;
  method: string;
  rowsWithData: number;
  rows: DwuDayRow[];
  sourceTextPreview: string;
}
export interface DwuConfirmRow {
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  delegation?: number | null;
  location?: string | null;
  notes?: string | null;
}
export interface BulkExtractResult {
  filename: string;
  aiEmployeeName?: string | null;
  matchedEmployeeId?: number | null;
  matchedEmployeeName?: string | null;
  provider?: string | null;
  method?: string | null;
  rows: DwuDayRow[];
  error?: string | null;
}

// ── Finanse / Faktury (V84) ─────────────────────────────────────────────
export type InvoiceDirection = 'PURCHASE' | 'SALES';
export type InvoicePaymentStatus =
  | 'DO_ZAPLATY' | 'CZESCIOWO_OPLACONA' | 'OPLACONA' | 'DO_WERYFIKACJI' | 'SPORNA' | 'ANULOWANA';
export type BankTxMatchStatus = 'UNMATCHED' | 'SUGGESTED' | 'CONFIRMED' | 'IGNORED';

export interface Invoice {
  id: number;
  direction: InvoiceDirection;
  directionName: string;
  source: 'KSEF' | 'MANUAL';
  ksefNumber?: string | null;
  invoiceNumber?: string | null;
  issueDate?: string | null;
  saleDate?: string | null;
  dueDate?: string | null;
  sellerNip?: string | null;
  sellerName?: string | null;
  buyerNip?: string | null;
  buyerName?: string | null;
  ourCompanyId?: number | null;
  ourCompanyName?: string | null;
  counterpartyClientId?: number | null;
  counterpartyClientName?: string | null;
  currency: string;
  totalNet?: number | null;
  totalVat?: number | null;
  totalGross: number;
  paymentStatus: InvoicePaymentStatus;
  paymentStatusName: string;
  paymentStatusColor: string;
  paidAmount: number;
  remainingAmount: number;
  paidDate?: string | null;
  paymentNote?: string | null;
  notes?: string | null;
}

export interface BankTransaction {
  id: number;
  bookingDate: string;
  amount: number;
  currency: string;
  counterpartyName?: string | null;
  counterpartyAccount?: string | null;
  title?: string | null;
  bankRef?: string | null;
  matchStatus: BankTxMatchStatus;
  matchStatusName: string;
  matchStatusColor: string;
  matchedInvoiceId?: number | null;
  matchedInvoiceNumber?: string | null;
  matchedInvoiceGross?: number | null;
  sourceFile?: string | null;
}

/** Wpis historii faktury (projekcja audytu bez hashy łańcucha) — podgląd faktury. */
export interface InvoiceHistoryEntry {
  id: number;
  at: string;                       // ISO-8601
  eventType: string;                // EventType (INVOICE_IMPORT, PAYMENT_STATUS_CHANGE, …)
  actorLogin?: string | null;
  actorName?: string | null;
  impersonatorLogin?: string | null; // akcja wykonana w trybie „pracuj jako"
  description?: string | null;
}

/** Komplet danych strony „Podgląd faktury" (/faktury/:id). */
export interface InvoiceDetail {
  invoice: Invoice;
  payments: BankTransaction[];      // przelewy z wyciągu wskazujące tę fakturę
  history: InvoiceHistoryEntry[];
}

export interface KsefLastSync {
  at: string;                       // ISO-8601 ostatniej PRÓBY (dowolny wynik)
  successAt?: string | null;        // ISO-8601 ostatniej UDANEJ synchronizacji (zachowane mimo błędu)
  status: 'OK' | 'ERROR' | 'SKIPPED';
  trigger: 'AUTO' | 'MANUAL';       // automatyczna (cron) / ręczna
  purchase: number;
  sales: number;
  message?: string;
}

export interface InvoiceMeta {
  ksefMode: string;
  directions: { id: InvoiceDirection; name: string; color: string }[];
  paymentStatuses: { id: InvoicePaymentStatus; name: string; color: string }[];
  lastSync?: KsefLastSync | null;
}

export interface PaymentStatusInput {
  status: InvoicePaymentStatus;
  paidAmount?: number | null;
  paidDate?: string | null;
  note?: string | null;
}

export interface BankLastSync {
  at: string;                       // ISO-8601 ostatniej próby
  successAt?: string | null;        // ISO-8601 ostatniej udanej synchronizacji
  status: 'OK' | 'ERROR' | 'SKIPPED';
  trigger: 'AUTO' | 'MANUAL';
  imported: number;
  matched: number;
  skipped: number;
  message?: string;
}

export interface BankMeta {
  ersteMode: string;                // mock | test | prod | off
  lastSync?: BankLastSync | null;
}

/** Faktura wprowadzana ręcznie — dostawcy spoza KSeF (zagranica) i dokumenty papierowe. */
export interface ManualInvoiceInput {
  direction: InvoiceDirection;
  invoiceNumber: string;
  issueDate?: string | null;
  saleDate?: string | null;
  dueDate?: string | null;
  sellerNip?: string | null;
  sellerName?: string | null;
  buyerNip?: string | null;
  buyerName?: string | null;
  ourCompanyId?: number | null;
  counterpartyClientId?: number | null;
  currency: string;
  totalNet?: number | null;
  totalVat?: number | null;
  totalGross: number;
  notes?: string | null;
}

// ── Subskrypcje i płatności cykliczne (V153) ────────────────────────────
export type SubscriptionCategoryCode =
  | 'INSURANCE' | 'TAX' | 'LICENSE' | 'SERVICE' | 'MEMBERSHIP' | 'LEASE' | 'UTILITY' | 'HOSTING' | 'OTHER';
export type BillingCycleCode = 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY' | 'BIENNIAL';
export type SubscriptionStatusCode = 'ACTIVE' | 'PAUSED' | 'ENDED';
export type SubscriptionPeriodStatusCode = 'PLANOWANY' | 'OPLACONY' | 'POMINIETY';
export type SubscriptionPaymentMethodCode = 'TRANSFER' | 'CARD' | 'DIRECT_DEBIT' | 'OTHER';
/** Pilność terminu — wspólna skala dla subskrypcji i jej okresów (kolorowanie wierszy). */
export type PaymentUrgency = 'OK' | 'DUE_SOON' | 'OVERDUE' | 'SETTLED';

export interface Vendor {
  id: number;
  name: string;
  taxId?: string | null;
  country: string;
  foreign: boolean;                 // spoza PL → faktury nie będzie w KSeF
  website?: string | null;
  contactEmail?: string | null;
  phone?: string | null;
  notes?: string | null;
  active: boolean;
  clientId?: number | null;
  clientName?: string | null;
}

export interface VendorInput {
  name: string;
  taxId?: string | null;
  country?: string | null;
  website?: string | null;
  contactEmail?: string | null;
  phone?: string | null;
  notes?: string | null;
  active?: boolean | null;
  clientId?: number | null;
}

export interface Subscription {
  id: number;
  name: string;
  category: SubscriptionCategoryCode;
  categoryName: string;
  categoryColor: string;
  referenceNumber?: string | null;
  description?: string | null;
  vendorId?: number | null;
  vendorName?: string | null;
  vendorForeign: boolean;
  ourCompanyId?: number | null;
  ourCompanyName?: string | null;
  billingCycle: BillingCycleCode;
  billingCycleName: string;
  startDate: string;
  endDate?: string | null;
  nextDueDate?: string | null;
  plannedAmount?: number | null;
  yearlyAmount?: number | null;     // kwota okresu przeliczona na rok
  currency: string;
  paymentMethod: SubscriptionPaymentMethodCode;
  paymentMethodName: string;
  paymentAutomatic: boolean;
  remindDaysBefore?: number | null;
  invoiceExpected: boolean;
  autoRenew: boolean;
  status: SubscriptionStatusCode;
  statusName: string;
  statusColor: string;
  notes?: string | null;
  daysUntil?: number | null;
  urgency: PaymentUrgency;
  openPeriods: number;
  overduePeriods: number;
  missingInvoices: number;
}

export interface SubscriptionPeriod {
  id: number;
  subscriptionId?: number | null;
  subscriptionName?: string | null;
  periodFrom: string;
  periodTo: string;
  dueDate: string;
  plannedAmount?: number | null;
  currency: string;
  status: SubscriptionPeriodStatusCode;
  statusName: string;
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  invoiceGross?: number | null;
  invoiceCurrency?: string | null;   // waluta faktury bywa inna niż waluta okresu
  invoicePaymentStatus?: InvoicePaymentStatus | null;
  invoicePaymentStatusName?: string | null;
  invoicePaymentStatusColor?: string | null;
  invoiceMissing: boolean;
  paid: boolean;
  paidSource?: 'FAKTURA' | 'RECZNIE' | null;
  paidDate?: string | null;
  paidAmount?: number | null;
  daysUntil: number;
  urgency: PaymentUrgency;
  notes?: string | null;
}

export interface SubscriptionDetail {
  subscription: Subscription;
  periods: SubscriptionPeriod[];
}

export interface SubscriptionInput {
  name: string;
  category: SubscriptionCategoryCode;
  referenceNumber?: string | null;
  description?: string | null;
  vendorId: number;
  ourCompanyId?: number | null;
  billingCycle: BillingCycleCode;
  startDate: string;
  endDate?: string | null;
  plannedAmount?: number | null;
  currency?: string | null;
  paymentMethod?: SubscriptionPaymentMethodCode | null;
  remindDaysBefore?: number | null;
  invoiceExpected?: boolean | null;
  autoRenew?: boolean | null;
  status?: SubscriptionStatusCode | null;
  notes?: string | null;
}

export interface SubscriptionMeta {
  categories: { id: SubscriptionCategoryCode; name: string; color: string; invoiceExpectedByDefault: boolean }[];
  cycles: { id: BillingCycleCode; name: string; months: number }[];
  statuses: { id: SubscriptionStatusCode; name: string; color: string }[];
  paymentMethods: { id: SubscriptionPaymentMethodCode; name: string; automatic: boolean }[];
}

/** Propozycja faktury z KSeF do okresu — do zatwierdzenia kliknięciem, nie przypinana sama. */
export interface InvoiceCandidate {
  invoice: Invoice;
  score: number;                    // 0–100
  reason: string;                   // np. „NIP sprzedawcy · kwota zgodna"
}

// ── Urlopy (V83) ────────────────────────────────────────────────────────
export type LeaveTypeCode =
  | 'WYPOCZYNKOWY' | 'NA_ZADANIE' | 'ZA_SWIETO' | 'OKOLICZNOSCIOWY'
  | 'BEZPLATNY' | 'OPIEKA_188' | 'L4' | 'INNE';
export type LeaveStatusCode = 'PLANNED' | 'APPROVED' | 'CANCELLED';

export interface LeaveRequest {
  id: number;
  employeeId: number;
  employeeName: string;
  leaveType: LeaveTypeCode;
  leaveTypeName: string;
  color: string;
  status: LeaveStatusCode;
  statusName: string;
  statusColor: string;
  startDate: string;
  endDate: string;
  halfDay: boolean;
  workingDays: number;
  /** Data podpisania papierowego wniosku urlopowego; null — wniosek niepodpisany (V140). */
  signedAt?: string | null;
  note?: string | null;
}

export interface LeaveBalance {
  year: number;
  seniorityYears: number | null;
  fte: number;
  annualDays: number;
  annualDaysAuto: number;
  annualOverridden: boolean;
  carriedOverDays: number;
  usedVacationDays: number;
  remainingVacationDays: number;
  onDemandLimit: number;
  onDemandUsedDays: number;
  onDemandRemainingDays: number;
  saturdayHolidayDays: number;
  saturdayHolidayUsedDays: number;
  saturdayHolidayRemainingDays: number;
  usedByType: Record<string, number>;
}

export interface LeaveOverview {
  balance: LeaveBalance;
  requests: LeaveRequest[];
}

export interface LeaveInput {
  leaveType: LeaveTypeCode;
  status?: LeaveStatusCode;
  startDate: string;
  endDate: string;
  halfDay?: boolean;
  note?: string | null;
}

export interface EntitlementInput {
  seniorityYears?: number | null;
  annualDays?: number | null;
  carriedOverDays?: number | null;
  note?: string | null;
}

export interface LeaveMeta {
  leaveTypes: { id: LeaveTypeCode; name: string; color: string; reducesVacation: boolean; onDemand: boolean; saturdayHolidayBack: boolean }[];
  leaveStatuses: { id: LeaveStatusCode; name: string; color: string }[];
}

// ── Pojazdy / Sprzęt ────────────────────────────────────────────────────
export type VehicleType = 'CAR' | 'VAN' | 'TRUCK' | 'TRAILER' | 'MACHINE' | 'EQUIPMENT';
export type VehicleStatus = 'ACTIVE' | 'IN_SERVICE' | 'RETIRED';
export type VehicleServiceType =
  | 'TECHNICAL_INSPECTION' | 'INSURANCE_OC' | 'INSURANCE_AC' | 'TACHOGRAPH'
  | 'UDT' | 'SERVICE' | 'FIRE_EXT' | 'OTHER';
export type VehicleServiceStatus = 'VALID' | 'EXPIRED' | 'ARCHIVED';
export type OwnershipType = 'OWNED' | 'LEASING' | 'RENTAL' | 'LOAN';
export type OdometerSource = 'MANUAL' | 'PHOTO' | 'SPREADSHEET' | 'IMPORT';
export type DocumentCategory =
  | 'REGISTRATION' | 'INSURANCE' | 'TECHNICAL_INSPECTION' | 'UDT' | 'LEASING'
  | 'INVOICE' | 'TACHOGRAPH' | 'ADR' | 'FUEL_CARD' | 'ETOLL' | 'PERMIT'
  | 'MANUAL' | 'PHOTO' | 'OTHER';

export interface VehicleOdometerReading {
  id: number;
  readingDate: string;
  odometer?: number | null;
  engineHours?: number | null;
  component?: string | null;
  source: OdometerSource;
  sourceName: string;
  note?: string | null;
}

export interface VehicleOdometerReadingInput {
  readingDate: string;
  odometer?: number | null;
  engineHours?: number | null;
  component?: string | null;
  source?: OdometerSource;
  note?: string | null;
}

export interface VehicleService {
  id: number;
  type: VehicleServiceType;
  typeName: string;
  name?: string | null;
  lastDate?: string | null;
  nextDate?: string | null;
  odometerAtService?: number | null;
  remindDaysBefore: number;
  cost?: number | null;
  status: VehicleServiceStatus;
  statusName: string;
  statusColor: string;
  notes?: string | null;
  daysUntil?: number | null;
  urgency: Urgency;
}

export interface Vehicle {
  id: number;
  type: VehicleType;
  typeName: string;
  label: string;
  brand?: string | null;
  model?: string | null;
  registrationNumber?: string | null;
  vin?: string | null;
  productionYear?: number | null;
  acquisitionDate?: string | null;
  odometer?: number | null;
  status: VehicleStatus;
  statusName: string;
  statusColor: string;
  assignedEmployeeId?: number | null;
  assignedEmployeeName?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  fuelCardNumber?: string | null;
  etollId?: string | null;
  ownershipType?: OwnershipType | null;
  ownershipTypeName?: string | null;
  financingCompany?: string | null;
  financingContractNo?: string | null;
  financingMonthlyCost?: number | null;
  insurer?: string | null;
  adrValidUntil?: string | null;
  /** Ręcznie wskazany lokalizator GPS (V159); null = dopasowanie automatyczne po nr rejestracyjnym. */
  gpsDeviceId?: string | null;
  notes?: string | null;
  services: VehicleService[];
  nearestDaysUntil?: number | null;
  nearestUrgency: Urgency;
  nearestDeadlineLabel?: string | null;
}

export interface VehicleInput {
  type: VehicleType;
  label: string;
  brand?: string | null;
  model?: string | null;
  registrationNumber?: string | null;
  vin?: string | null;
  productionYear?: number | null;
  acquisitionDate?: string | null;
  odometer?: number | null;
  status?: VehicleStatus;
  assignedEmployeeId?: number | null;
  companyId?: number | null;
  fuelCardNumber?: string | null;
  etollId?: string | null;
  ownershipType?: OwnershipType | null;
  financingCompany?: string | null;
  financingContractNo?: string | null;
  financingMonthlyCost?: number | null;
  insurer?: string | null;
  adrValidUntil?: string | null;
  gpsDeviceId?: string | null;
  notes?: string | null;
}

export interface VehicleServiceInput {
  type: VehicleServiceType;
  name?: string | null;
  lastDate?: string | null;
  nextDate?: string | null;
  odometerAtService?: number | null;
  remindDaysBefore?: number | null;
  cost?: number | null;
  status?: VehicleServiceStatus;
  notes?: string | null;
}

export interface VehicleMeta {
  types: { id: VehicleType; name: string }[];
  statuses: { id: VehicleStatus; name: string; color: string }[];
  serviceTypes: { id: VehicleServiceType; name: string }[];
  serviceStatuses: { id: VehicleServiceStatus; name: string; color: string }[];
  ownershipTypes: { id: OwnershipType; name: string }[];
  docCategories: { id: DocumentCategory; name: string }[];
  odometerSources: { id: OdometerSource; name: string }[];
}

// ── Kalendarz brygad ────────────────────────────────────────────────────
export type TripPurpose = 'DRILLING' | 'MEASUREMENT' | 'SERVICE' | 'TRANSPORT' | 'OTHER';
export type TripStatus = 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type TripRole = 'DRIVER' | 'OPERATOR' | 'GEOLOGIST' | 'ASSISTANT' | 'OTHER';

export interface TripAssignmentRef {
  id: number;
  employeeId?: number | null;
  employeeName?: string | null;
  role: TripRole;
  roleName: string;
}

export interface TripVehicleRef {
  id: number;
  vehicleId?: number | null;
  label?: string | null;
  registrationNumber?: string | null;
}

export interface Trip {
  id: number;
  title: string;
  purpose: TripPurpose;
  purposeName: string;
  purposeColor: string;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  locationText?: string | null;
  intakeId?: number | null;
  intakeName?: string | null;
  studyId?: number | null;
  studyName?: string | null;
  brigadeId?: number | null;
  brigadeName?: string | null;
  brigadeColor?: string | null;
  status: TripStatus;
  statusName: string;
  statusColor: string;
  notes?: string | null;
  createdById?: number | null;
  createdByName?: string | null;
  assignments: TripAssignmentRef[];
  vehicles: TripVehicleRef[];
  color?: string | null;
}

export interface TripInput {
  title: string;
  purpose: TripPurpose;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  locationText?: string | null;
  intakeId?: number | null;
  studyId?: number | null;
  brigadeId?: number | null;
  status?: TripStatus;
  notes?: string | null;
  assignments: { employeeId: number; role: TripRole }[];
  vehicleIds: number[];
}

export interface TripMeta {
  purposes: { id: TripPurpose; name: string; color: string }[];
  statuses: { id: TripStatus; name: string; color: string }[];
  roles: { id: TripRole; name: string }[];
}

export interface Brigade {
  id: number;
  name: string;
  color: string;
  active: boolean;
}

export interface BrigadeInput {
  name: string;
  color?: string | null;
  active?: boolean | null;
}

export interface UpcomingTrip {
  id: number;
  title: string;
  startDate: string;
  endDate: string;
  purposeName?: string | null;
  statusName?: string | null;
  statusColor?: string | null;
  locationText?: string | null;
  brigadeName?: string | null;
  color?: string | null;
  crewCount: number;
  vehicleCount: number;
}

// ── Konfigurowalny dashboard (widgety, V78) ──────────────────────────────
export type DashboardWidgetWidth = 'kpi' | 'half' | 'full';

/** Jeden widget w układzie dashboardu użytkownika: klucz + widoczność + szerokość (col-span). */
export interface DashboardLayoutItem {
  key: string;
  visible: boolean;
  width: DashboardWidgetWidth;
}

// ── Analiza sitowa / dobór obsypki i szczeliny filtra (V86) ──────────────
export type SieveProjectStatus = 'DRAFT' | 'ACTIVE' | 'DONE';
export type SieveDecisionBasis = 'PN' | 'DVGW' | 'EXP';

export interface SieveProjectSummary {
  id: number;
  name: string;
  docNo?: string | null;
  intakeId?: number | null;
  intakeName?: string | null;
  /** Powiązany otwór (V164) — pozwala filtrować listę do jednego otworu. */
  boreholeId?: number | null;
  well?: string | null;
  sampleCount: number;
  studyDate?: string | null;
  status: SieveProjectStatus;
  statusName: string;
  statusColor: string;
  createdAt?: string | null;
}

export interface SievePn {
  type: 'pack' | 'no';
  fracLabel?: string | null;
  altLabel?: string | null;
  wMax?: number | null;
  slot?: number | null;
  slotNo?: number | null;
  /** W przedziale d50/3 … d50 nie mieści się żadna znormalizowana szczelina PVC-U (min. 0,5 mm). */
  slotNoNone: boolean;
  thick?: number | null;
  r0?: number | null;
  r1?: number | null;
  withPack: boolean;
  noPack: boolean;
  fracLo?: number | null;
  fracHi?: number | null;
  resFrac: string;
  resSlot: string;
}

export interface SieveDvgw {
  fg?: number | null;
  ds?: number | null;
  natural: boolean;
  groupLabel?: string | null;
  slotTxt?: string | null;
  slotMin?: number | null;
  slotMax?: number | null;
  groupLo?: number | null;
  groupHi?: number | null;
  thick?: number | null;
  /** Ds poza tabelą DIN 4924: 'high' (> 16 mm) / 'low' (< 0,71 mm) / null. */
  outOfTable?: 'high' | 'low' | null;
  /** Grupa skrajna proponowana jako ekstrapolacja poza normę (margines ×1,25). */
  extrapLabel?: string | null;
  extrapSlotTxt?: string | null;
  dinLo?: number | null;
  dinHi?: number | null;
  resFrac: string;
  /** ETYKIETA z tab. 1 W 113 — dla większości grup DIN jest ZAKRESEM („1,0 – 1,5"). Tylko do wyświetlenia. */
  resSlot: string;
  /** Pojedyncza znormalizowana szczelina do zapisania w decyzji (null przy filtrze naturalnym). */
  resSlotValue?: string | null;
}

export type SieveKfMethod = 'beyer' | 'hazen' | 'usbsc';

/** Wybrana metoda współczynnika filtracji + zakresy stosowania wszystkich trzech. */
export interface SieveKf {
  method: SieveKfMethod;
  name: string;
  value?: number | null;
  text: string;
  md: string;
  /** true = dobrana automatycznie, false = nadpisana przez geologa. */
  auto: boolean;
  /** true = wybrana metoda jest poza swoim zakresem stosowania. */
  warn: boolean;
  fitsBeyer: boolean;
  fitsHazen: boolean;
  fitsUsbsc: boolean;
}

export type SieveTerzState = 'ok' | 'lim' | 'no';

/** Kryteria Terzaghiego dla jednej ścieżki doboru (PN albo DVGW). */
export interface SieveTerz {
  d15?: number | null;
  u?: number | null;
  retention?: number | null;
  retentionState: SieveTerzState;
  permeability?: number | null;
  permeabilityState: SieveTerzState;
  text: string;
}

export interface SieveResult {
  tot: number;
  pct: number[];
  pass: number[];
  d10?: number | null;
  d15?: number | null;
  d20?: number | null;
  d30?: number | null;
  d50?: number | null;
  d60?: number | null;
  d85?: number | null;
  dg?: number | null;
  cu?: number | null;
  cc?: number | null;
  suff: boolean;
  peaks: number;
  kfBeyer?: number | null;
  kfHazen?: number | null;
  kfUsbsc?: number | null;
  kfBeyerText: string;
  kfBeyerMd: string;
  kfHazenText: string;
  kfHazenMd: string;
  kfUsbscText: string;
  kfUsbscMd: string;
  kf: SieveKf;
  soilSym: string;
  soilName: string;
  pn: SievePn;
  dvgw: SieveDvgw;
  concord: boolean;
  suggestedFraction: string;
  suggestedSlot: string;
  terzPn?: SieveTerz | null;
  terzDvgw?: SieveTerz | null;
  dvgwOutOfTableMessage?: string | null;
  guide: string[];
}

export interface SieveSample {
  id: number;
  ordinal: number;
  depthFrom?: number | null;
  depthTo?: number | null;
  masses: number[];
  decFraction?: string | null;
  decSlot?: string | null;
  decBasis: SieveDecisionBasis;
  decBasisName?: string | null;
  decInclude: boolean;
  decNote?: string | null;
  decSaved: boolean;
  /** Nadpisanie metody k przez geologa; null = dobór automatyczny. */
  decKfMethod?: SieveKfMethod | null;
  result: SieveResult;
}

export interface SieveProject {
  id: number;
  name: string;
  docNo?: string | null;
  intakeId?: number | null;
  intakeLabel?: string | null;
  /** Otwór, z którego pochodzą próbki (V164) — opcjonalny, FK z ON DELETE SET NULL. */
  boreholeId?: number | null;
  boreholeLabel?: string | null;
  clientId?: number | null;
  clientName?: string | null;
  intakeName?: string | null;
  town?: string | null;
  gmina?: string | null;
  powiat?: string | null;
  woj?: string | null;
  well?: string | null;
  studyDate?: string | null;
  performedBy?: string | null;
  geologName?: string | null;
  geologLic?: string | null;
  reportDate?: string | null;
  dvgwIntermittent: boolean;
  status: SieveProjectStatus;
  statusName: string;
  statusColor: string;
  notes?: string | null;
  samples: SieveSample[];
}

export interface SieveProjectInput {
  name: string;
  docNo?: string | null;
  intakeId?: number | null;
  /** Otwór — backend uzupełni z niego `well` i ujęcie, jeśli ich nie podano. */
  boreholeId?: number | null;
  clientId?: number | null;
  intakeName?: string | null;
  town?: string | null;
  gmina?: string | null;
  powiat?: string | null;
  woj?: string | null;
  well?: string | null;
  studyDate?: string | null;
  performedBy?: string | null;
  geologName?: string | null;
  geologLic?: string | null;
  reportDate?: string | null;
  dvgwIntermittent?: boolean | null;
  status?: SieveProjectStatus | null;
  notes?: string | null;
}

export interface SieveSampleInput {
  depthFrom?: number | null;
  depthTo?: number | null;
  masses: number[];
}

export interface SieveDecisionInput {
  fraction?: string | null;
  slot?: string | null;
  basis?: SieveDecisionBasis | null;
  include?: boolean | null;
  note?: string | null;
  saved?: boolean | null;
  /**
   * Nadpisanie metody k. Pusty string = JAWNE wyczyszczenie (powrót do doboru automatycznego).
   * null / pominięcie pola = „nie zmieniaj" — backend rozróżnia te dwa przypadki.
   */
  kfMethod?: SieveKfMethod | '' | null;
}

export interface SieveExtraction {
  provider: string;
  model: string;
  method: string;
  depthFrom?: number | null;
  depthTo?: number | null;
  masses: number[];
  sourceTextPreview?: string | null;
}

export interface SieveOption { value: string; label: string; }
export interface SieveFractionMeta { ord: number; upperMm: number; label: string; }
/** Które sekcje trafiają do raportu PDF i w jakiej orientacji. */
export interface SieveReportOptions {
  params: boolean;
  comparison: boolean;
  decisions: boolean;
  methodology: boolean;
  landscape: boolean;
}

/** Wynik zapisania raportu w archiwum. */
export interface SieveReportArchiveResult {
  documentId?: number | null;
  filename: string;
  duplicate: boolean;
  attached: boolean;
  /** Ile raportów tego opracowania było w archiwum PRZED tym zapisem. */
  poprzednich: number;
  note?: string | null;
}

export interface SieveMeta {
  statuses: { id: SieveProjectStatus; name: string; color: string }[];
  bases: { id: SieveDecisionBasis; name: string }[];
  fractions: SieveFractionMeta[];
  fractionOptions: SieveOption[];
  slotOptions: SieveOption[];
}

// ── Ocena ryzyka (art. 4f/4g/4i) ─────────────────────────────────────────
export type RiskLayerCode = 'UJECIE' | 'SYSTEM' | 'INSTALACJA';
export type RiskAssessmentStatusCode =
  | 'NOWE' | 'OCZEKUJE_NA_UZUPELNIENIE' | 'W_KOLEJCE' | 'W_PRZYGOTOWANIU' | 'DO_WERYFIKACJI'
  | 'DO_RECENZJI' | 'RECENZJA' | 'ZWROCONY' | 'PODPISANY' | 'PRZEKAZANY' | 'ANULOWANY';
export type HazardCategoryCode = 'MIKROBIOLOGICZNE' | 'CHEMICZNE' | 'FIZYCZNE' | 'RADIOLOGICZNE' | 'KLIMATYCZNE';
export type ControlTypeCode = 'PRAWNE' | 'TECHNICZNE' | 'FINANSOWE' | 'ORGANIZACYJNE' | 'NAPRAWCZE';
export type ControlResponsibilityCode = 'DOSTAWCA' | 'ORGAN' | 'WSPOLNIE' | 'INNY';
export type MonitoringTypeCode = 'OPERACYJNY' | 'ZGODNOSCI';
export type HazardStatusCode = 'PROPONOWANE' | 'ZAAKCEPTOWANE' | 'ODRZUCONE';
export type NarrativeStatusCode = 'PUSTA' | 'WYGENEROWANA' | 'W_EDYCJI' | 'ZAAKCEPTOWANA';
export type RiskReviewerRoleCode = 'SPECJALISTA' | 'RECENZENT' | 'OSOBA_UPRAWNIONA' | 'PODWYKONAWCA';

// ── Faza 1 automatyzacji: obszar zasilania / ledger dowodów / rejestr luk ─────
/** Wgrana granica strefy ochronnej ujęcia (F4) — mapa oceny + karta stref. */
export interface RiskProtectionZone {
  kind: 'BEZPOSREDNIA' | 'POSREDNIA' | string;
  kindName: string;
  areaKm2: string | null;
  geoJson: string;
}

export interface RiskCatchment {
  strategy: string; provisional: boolean; confidence: string;
  radiusM: number | null; areaM2: number | null; areaKm2: number | null; geoJson: string | null;
}
export interface RiskGap {
  id: number; fieldKey: string | null; label: string; reason: string | null;
  requiredFrom: string; blocking: boolean; resolved: boolean;
}
export interface RiskEvidenceFact {
  id: number; factKey: string; kind: string; label: string | null;
  value: string | null; sourceDataset: string | null; distanceM: number | null; fetchedAt: string | null;
  /** Krok procesu 5-krokowego, który pozyskał fakt (OBSZAR | ZAGROZENIA | PROGRAM_BADAN | …). */
  stage: string | null;
}
export interface RiskPressureOverlay {
  id: number; sourceLayer: string;
  /** Krótka polska nazwa warstwy („Drogi") zamiast kodu („BDOT_ROAD"). */
  layerLabel: string | null;
  featureId: string | null;
  /** Surowa nazwa ze źródła (kod CODE_18 / klasa GML) — proweniencja. */
  featureName: string | null;
  /** Czytelna nazwa ze słownika CLC/BDOT10k; fallback na surową. */
  featureLabel: string | null;
  distanceM: number | null; overlapFraction: number | null; intersects: boolean;
}
export interface RiskChecklistItem {
  gapId: number | null; kind: string; label: string; detail: string | null;
  blocking: boolean; resolved: boolean;
}
export interface RiskSourceStatus {
  code: string; name: string;
  /** KARTY = karty charakterystyki JCWPd (aPGW) — dane per kod jednolitej części wód, bez geometrii.
   *  MONITORING = punkty i oznaczenia z bazy MWP (PIG-PIB PSH) — status czytany z rejestru dowodów,
   *  bo ingest nie zapisuje ani do intake_lookup_cache, ani do local_geom_feature. */
  category: 'PUNKTOWE' | 'PRESJE' | 'KARTY' | 'MONITORING' | 'NIEDOSTEPNE' | string;
  status: 'OK' | 'BLAD' | 'PUSTE' | 'NIE_POBRANO' | 'NIEDOSTEPNE' | string;
  reachable: boolean; featureCount: number | null; detail: string | null; fetchedAt: string | null;
  /** Zakres pobrania warstwy presji „dla tej oceny": POINT (koperta), GLOBAL (import krajowy),
   *  UPLOAD (ręczne wgranie GeoJSON — źródła bez dostępu maszynowego) lub null. */
  fetchScope: 'POINT' | 'GLOBAL' | 'UPLOAD' | null;
  /** Czy warstwę można zasilić ręcznym wgraniem GeoJSON. NIEZALEŻNE od fetchScope: SEVESO i E-PRTR
   *  mają konektor maszynowy (GLOBAL), a upload zostaje ścieżką awaryjną przy niedostępności GIOŚ. */
  uploadable: boolean;
  /** Krok procesu 5-krokowego konsumujący to źródło (OBSZAR | ZAGROZENIA | PROGRAM_BADAN). */
  stage: string | null;
}
export interface RiskPressureFeatureGeo {
  sourceLayer: string; featureName: string | null; featureLabel: string | null;
  /** Klucz łączący obiekt na mapie z wierszem listy presji. */
  overlayId: number | null;
  distanceM: number | null; intersects: boolean; geoJson: string;
}
export interface RiskAssessmentWell {
  id: number;
  /** INTAKE = z powiązanego ujęcia (read-only); ASSESSMENT = wpisana na ocenie (edytowalna). */
  source: 'INTAKE' | 'ASSESSMENT' | string;
  designation: string;
  function: string | null;
  functionName: string | null;
  latitude: number | null;
  longitude: number | null;
  yieldM3h: number | null;
}
export interface RiskPressureAttr { key: string; label: string; value: string }
export interface RiskPressureDetail {
  id: number; sourceLayer: string; layerLabel: string | null;
  featureId: string | null; featureName: string | null; featureLabel: string | null;
  distanceM: number | null; overlapFraction: number | null; intersects: boolean;
  attributes: RiskPressureAttr[];
  /** Zagrożenia wyprowadzone DOKŁADNIE z tego obiektu (puste, gdy reguła wybrała inny). */
  hazards: RiskHazard[];
  geoJson: string | null;
}
export interface RiskGenerationStage {
  stageKey: string; label: string;
  status: 'PENDING' | 'RUNNING' | 'OK' | 'FAILED' | string;
  sortOrder: number; message: string | null;
  startedAt: string | null; finishedAt: string | null;
}

export interface RiskAssessmentSummary {
  id: number; number: string; title: string;
  layer: RiskLayerCode; layerName: string; layerColor: string; legalBasis: string;
  status: RiskAssessmentStatusCode; statusName: string; statusColor: string;
  intakeId?: number | null; intakeName?: string | null;
  clientId?: number | null; clientName?: string | null;
  objectName?: string | null; town?: string | null;
  deadline?: string | null; daysToDeadline?: number | null;
  criticalFlag: boolean; hazardCount: number; criticalCount: number;
  reviewerName?: string | null; fromHydronexis: boolean; createdAt: string;
}

export interface RiskControl {
  id: number; ordinal: number; type: ControlTypeCode; typeName: string;
  description: string; existing: boolean;
  responsibility: ControlResponsibilityCode;
  /** Etykieta odpowiedzialnego — dla INNY jest to nazwa podmiotu, nie słowo „Inny podmiot". */
  responsibilityName: string;
  /** Nazwa podmiotu, gdy responsibility = INNY (uwaga 19). */
  responsibilityOther?: string | null;
  implementationDeadline?: string | null; reviewIntervalMonths?: number | null; aiGenerated: boolean;
}

export interface RiskHazard {
  id: number; category: HazardCategoryCode; categoryName: string; categoryColor: string;
  title: string; description?: string | null; location?: string | null;
  /** Inwentarz F3 (art. 2 pkt 19a/22): sektor · nośnik zagrożenia · zdarzenie niebezpieczne. */
  sector?: string | null; hazardCarrier?: string | null; triggerEvent?: string | null;
  /** Uzasadnienie przyjętych P i D (uwaga 21) — propozycja AI weryfikowana przez eksperta. */
  riskJustification?: string | null;
  probabilityInitial: number; severityInitial: number; riskInitial: number;
  riskInitialLevel: string; riskInitialColor: string;
  probabilityResidual: number; severityResidual: number; riskResidual: number;
  riskResidualLevel: string; riskResidualColor: string;
  priority: number; status: HazardStatusCode; statusName: string; statusColor: string;
  aiGenerated: boolean;
  sourceLayer: string | null; distanceM: number | null; confidence: string | null; derivationRuleId: number | null;
  /** Punkt własnego zagrożenia wskazanego na mapie oceny (WGS84); null = brak lokalizacji. */
  latitude: number | null; longitude: number | null;
  controls: RiskControl[];
}

/** Rodzaj monitoringu OPERACYJNEGO (uwaga 26) — decyduje o etykietach i widoczności pól pozycji. */
export type OperationalKindCode =
  'BADANIA_LABORATORYJNE' | 'INSPEKCJA_TERENOWA' | 'MONITORING_CIAGLY' | 'KONTROLA_TECHNICZNA' | 'INNE';

export interface RiskOperationalKind {
  code: OperationalKindCode; name: string;
  parameterLabel: string; pointLabel: string;
  usesMatrix: boolean; usesLimitValue: boolean;
}

export interface RiskMonitoring {
  id: number; ordinal: number; type: MonitoringTypeCode; typeName: string;
  /** Rodzaj monitoringu operacyjnego; null dla pozycji zgodności. */
  operationalKind?: OperationalKindCode | null; operationalKindName?: string | null;
  parameter: string; frequency?: string | null; samplingPoint?: string | null; limitValue?: string | null;
  /** Pochodzenie: SZABLON (biblioteka) | REGULA (ryzyko-zależna z zagrożeń) | RECZNY. */
  origin?: string | null;
  /** Badana matryca: WODA_SUROWA | WODA_PODZIEMNA | WODA_POWIERZCHNIOWA. */
  matrix?: string | null;
  /** „Dlaczego ten parametr" — uzasadnienie doboru z odwołaniem do zagrożenia. */
  justification?: string | null;
  derivedFromHazardId?: number | null;
  derivedFromHazardTitle?: string | null;
  /** Środek kontroli, którego skuteczność potwierdza pozycja (uwaga 24). */
  controlId?: number | null;
  controlDescription?: string | null;
}

export interface RiskNarrativeSection {
  id: number; section: string; sortOrder: number;
  draftText?: string | null; editedText?: string | null;
  status: NarrativeStatusCode; statusName: string; statusColor: string;
  model?: string | null; sourcesJson?: string | null;
  /** TRUE gdy dane źródłowe rozdziału zmieniły się po jego wygenerowaniu/zapisie — „wymaga aktualizacji". */
  stale: boolean;
}

export interface RiskDocumentVersion {
  id: number; version: string; fileName: string; sizeBytes?: number | null; sha256?: string | null;
  signed: boolean; signedAt?: string | null; signerName?: string | null;
  retentionUntil?: string | null; createdAt: string;
}

/** Dokument źródłowy wgrany do oceny (archiwum + MinIO). `indexed` = tekst wyekstrahowany i czytelny dla AI. */
export interface RiskSourceDocument {
  id: number; name: string; sizeBytes?: number | null; pages?: number | null;
  indexed: boolean; ragEnabled: boolean; textChars?: number | null; createdAt: string;
}

/** Podsumowanie zbiorczego uploadu dokumentów źródłowych. */
export interface RiskSourceUploadSummary {
  attached: number; duplicates: number; errors: string[]; documents: RiskSourceDocument[];
}

// ── Etap 2: checklista wymaganych dokumentów + propozycje AI rozpoznania typu ──
export type RiskDocStatusCode = 'PENDING' | 'RECEIVED' | 'NOT_APPLICABLE' | 'WAIVED';

export interface RiskDocFile { id: number; name: string; sizeBytes?: number | null; }

export interface RiskDocItem {
  id: number; code: string; name: string; description?: string | null; legalBasis?: string | null;
  mandatory: boolean; sortOrder?: number | null;
  status: RiskDocStatusCode; statusName: string; note?: string | null;
  files: RiskDocFile[];
}

export interface RiskDocChecklist {
  assessmentId: number; total: number; received: number; pending: number; notApplicable: number; waived: number;
  items: RiskDocItem[];
}

/** Propozycja AI: dopasowanie wgranego pliku do pozycji wymaganych (człowiek zatwierdza/odrzuca). */
export interface RiskDocProposal {
  id: number; archiveDocumentId: number; archiveDocumentName: string;
  proposedItemId?: number | null; proposedItemCode?: string | null; proposedItemName?: string | null;
  confidence?: number | null; source?: string | null; status: string; explanation?: string | null;
}

export interface RiskReviewerAssignment {
  id: number; userId?: number | null; userName?: string | null;
  role: RiskReviewerRoleCode; roleName: string; note?: string | null; createdAt: string;
}

export interface RiskInputData {
  id: number; version: number; dataJson: string; complete: boolean;
  missingFieldsJson?: string | null; source: string; createdAt: string;
}

/** Raport importu paczki danych wejściowych ZIP z HydroNexis (CSEU) w oknie „Nowa ocena". */
export interface RiskPackageImportResult {
  assessment: RiskAssessment;
  wellsImported: number;
  hazardsImported: number;
  documentsAttached: number;
  documentsFailed: number;
  warnings: string[];
}

export interface RiskAssessment {
  id: number; number: string; title: string;
  layer: RiskLayerCode; layerName: string; layerColor: string; legalBasis: string;
  status: RiskAssessmentStatusCode; statusName: string; statusColor: string; locked: boolean;
  intakeId?: number | null; intakeName?: string | null;
  clientId?: number | null; clientName?: string | null;
  objectName?: string | null; town?: string | null; gmina?: string | null; powiat?: string | null; woj?: string | null;
  latitude?: number | null; longitude?: number | null;
  jcwpdCode?: string | null; jcwpdName?: string | null; gzwpNumber?: number | null; inGzwp?: boolean | null;
  deadline?: string | null; daysToDeadline?: number | null;
  requiredSignerQualification?: string | null; hydronexisOrderId?: string | null;
  sourceAssessmentId?: number | null; sourceAssessmentNumber?: string | null;
  dispatcherName?: string | null; specialistName?: string | null; reviewerName?: string | null;
  signedAt?: string | null; signerName?: string | null;
  revalidationDate?: string | null; handedOffAt?: string | null; completedAt?: string | null;
  criticalFlag: boolean; notes?: string | null;
  hazards: RiskHazard[]; monitoring: RiskMonitoring[]; sections: RiskNarrativeSection[];
  documents: RiskDocumentVersion[]; assignments: RiskReviewerAssignment[]; inputData?: RiskInputData | null;
}

export interface RiskMetaOption { code: string; name: string; [k: string]: unknown; }
export interface RiskInputField {
  key: string; label: string; kind: 'TEXT' | 'TEXTAREA' | 'NUMBER';
  group: string; requiredFor: RiskLayerCode[];
  /** Warstwy, w których pole ma sens; pusta lista = wspólne dla wszystkich (patrz InputField#appliesTo). */
  appliesTo: RiskLayerCode[];
  hint?: string | null;
}
/** Parametry metodyczno-prawne (RiskMethodologyConfig) — konfigurowalne pasma, terminy, normy. */
export interface RiskMethodology {
  matrixLowMax: number;
  matrixMediumMax: number;
  matrixHighMax: number;
  matrixScaleMax: number;
  /** LINIOWA (P×D) lub KWADRATOWA (P×D² — asymetria LAWA/DVGW). */
  severityScale?: 'LINIOWA' | 'KWADRATOWA' | string;
  deadlineCatchment: string;
  deadlineSystem: string;
  deadlineInstallation: string;
  revalidationYears: number;
  reportHandoffRequired: boolean;
  normsCatalog: string;
  observationList: string;
}
export interface RiskMeta {
  layers: { code: RiskLayerCode; name: string; legalBasis: string; color: string }[];
  statuses: { code: RiskAssessmentStatusCode; name: string; color: string }[];
  hazardCategories: { code: HazardCategoryCode; name: string; color: string }[];
  controlTypes: RiskMetaOption[];
  responsibilities: RiskMetaOption[];
  monitoringTypes: RiskMetaOption[];
  operationalKinds: RiskOperationalKind[];
  reviewerRoles: RiskMetaOption[];
  riskLevels: { code: string; name: string; hex: string; min: number; max: number }[];
  inputFields: RiskInputField[];
  methodology: RiskMethodology;
  assignableUsers: { id: number; name: string }[];
}

/** #3 Gotowość ujęcia do automatycznej oceny ryzyka. */
export interface IntakeRiskReadinessCheck {
  key: string; label: string; present: boolean; critical: boolean; hint: string;
}
export interface IntakeRiskReadiness {
  intakeId: number; intakeName: string; intakeNumber: string;
  total: number; present: number; criticalMissing: number; automationReady: boolean;
  checks: IntakeRiskReadinessCheck[];
}
